import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

/**
 * 경로별 검색용 제목·설명·canonical·JSON-LD (SEO ①-b).
 * 첫 화면은 서버가 이미 같은 값을 HTML 에 넣어 보낸다(SeoMetaController). 여기서는 화면 이동 때 같은 표
 * (public/seo-routes.json, 서버와 같은 파일)로 갈아 끼운다. 서버가 넣은 태그에는 data-rh 가 있어 중복되지 않는다.
 */
export type SeoRoute = {
  path: string;
  title: string;
  description: string;
  ogTitle?: string;
  ogDescription?: string;
  canonical: string;
  jsonLd?: unknown;
};

const ROUTES_URL = "/seo-routes.json";
let cached: Promise<SeoRoute[]> | null = null;

function loadRoutes(): Promise<SeoRoute[]> {
  if (!cached) {
    cached = fetch(ROUTES_URL)
      .then((r) => (r.ok ? r.json() : { routes: [] }))
      .then((doc) => (Array.isArray(doc?.routes) ? doc.routes : []))
      .catch(() => []);
  }
  return cached;
}

export function normalizePath(pathname: string): string {
  return pathname.length > 1 && pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
}

export function findRoute(routes: SeoRoute[], pathname: string): SeoRoute | undefined {
  const path = normalizePath(pathname);
  return routes.find((r) => r.path === path);
}

export default function SeoHead() {
  const location = useLocation();
  const [routes, setRoutes] = useState<SeoRoute[]>([]);

  useEffect(() => {
    let isActive = true;
    loadRoutes().then((r) => isActive && setRoutes(r));
    return () => {
      isActive = false;
    };
  }, []);

  const route = findRoute(routes, location.pathname);
  // Routes outside the table use the home entry for title/description/og (no canonical/JSON-LD).
  // Emitting description/og here too lets Helmet own those tags, so the untagged originals in a
  // server-sent plain index.html do not end up duplicated after navigating to a public page.
  const fallback = findRoute(routes, "/");
  if (!route) {
    return fallback ? (
      <Helmet>
        <title>{fallback.title}</title>
        <meta name="description" content={fallback.description} />
        <meta property="og:title" content={fallback.ogTitle || fallback.title} />
        <meta property="og:description" content={fallback.ogDescription || fallback.description} />
      </Helmet>
    ) : null;
  }
  return (
    <Helmet>
      <title>{route.title}</title>
      <meta name="description" content={route.description} />
      <meta property="og:title" content={route.ogTitle || route.title} />
      <meta property="og:description" content={route.ogDescription || route.description} />
      <meta property="og:url" content={route.canonical} />
      <link rel="canonical" href={route.canonical} />
      {route.jsonLd ? <script type="application/ld+json">{JSON.stringify(route.jsonLd)}</script> : null}
    </Helmet>
  );
}
